import { useEffect, useState } from 'react';

/**
 * Whether each linked project actually answered when it was last checked.
 *
 * /status.json is written by scripts/check-status.mjs on a daily cron and
 * committed, so this is a static file — no function, no rate limit, and it
 * costs one cached request no matter how many cards ask for it.
 *
 * Precedence, in order:
 *
 *   1. A fresh check saying the site answered      -> Live, with the time.
 *   2. A fresh check saying it did not, twice      -> Offline, and it stops
 *                                                     counting toward "N live".
 *   3. No file, no entry, or a check older than
 *      MAX_AGE_MS                                  -> fall back to the authored
 *                                                     status in projects.js.
 *
 * Rule 3 is the one that matters. A cron that quietly stops running would
 * otherwise freeze a page full of "Live" badges into a claim nobody is checking
 * any more — the exact failure this replaced.
 */

// Three days: the check runs daily, so this tolerates two missed runs before it
// stops trusting the file.
const MAX_AGE_MS = 3 * 24 * 60 * 60 * 1000;

// One request per page load, shared by every component that asks.
let inflight = null;

function load() {
  if (!inflight) {
    inflight = fetch('/status.json', { cache: 'no-cache' })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null);
  }
  return inflight;
}

export function useLiveStatus() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let active = true;
    load().then((d) => {
      if (active) setData(d);
    });
    return () => {
      active = false;
    };
  }, []);

  const fresh =
    Boolean(data?.checkedAt) && Date.now() - new Date(data.checkedAt).getTime() < MAX_AGE_MS;

  /**
   * 'live' | 'offline' | null — null means "no opinion, use what the project
   * says about itself".
   */
  const statusFor = (link) => {
    if (!fresh || !link) return null;
    const entry = data.sites?.[link];
    if (!entry || typeof entry.ok !== 'boolean') return null;
    return entry.ok ? 'live' : 'offline';
  };

  const checkedAtFor = (link) => (fresh ? data.sites?.[link]?.checkedAt || null : null);

  return {
    fresh,
    checkedAt: fresh ? data.checkedAt : null,
    liveCount: fresh && typeof data.liveCount === 'number' ? data.liveCount : null,
    statusFor,
    checkedAtFor,
  };
}

export default useLiveStatus;
